import { Helmet } from '@dr.pogodin/react-helmet';
import { endOfDay, parseISO, startOfDay } from 'date-fns';
import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { Api } from 'src/api';
import { PageError, PageLoading, PageWrap, Stack, Text } from 'src/components';
import { Color, Style } from 'src/constants';
import { color } from 'src/constants/color.css';
import { useClientData, useTimerChange } from 'src/contexts';
import { useMyUserClient, useNow } from 'src/hooks';
import { Model } from 'src/models';
import { formatAsDate, formatAsISO, formatTimeSpent } from 'src/util';

import {
  ActivityDateSelect,
  ActivityUserRow,
  ProjectsTabs,
} from '../components';
import { buildDayActivity, getSelectableDates } from '../util';

/** The running clocks only need to keep up with themselves to the second. */
const TICK_MS = 1000;

export default function DailyActivityPage() {
  const myUser = useMyUserClient();
  const { agencyCompanies } = useClientData();

  // Held still for the life of the page, so the day being shown doesn't shift
  // under the reader as the clock ticks past midnight.
  const [today] = React.useState(() => formatAsDate(new Date()));
  const [date, setDate] = React.useState(today);
  const isToday = date === today;
  const day = parseISO(date);

  // Only today has clocks still going.
  const now = useNow(isToday ? TICK_MS : null);

  const apiUsers = Api.User.useReadCollectionAgency();
  // Today is left open-ended so the running timers come back with it: they
  // have no loggedAt of their own to be found by, and the API files them as
  // after everything.
  const apiDay = Api.WorkDone.useReadCollection({
    'loggedAt[after]': formatAsISO(startOfDay(day)),
    'loggedAt[before]': isToday ? undefined : formatAsISO(endOfDay(day)),
  });

  useTimerChange(apiDay.invalidate);

  if (apiUsers.error) return <PageError {...apiUsers} />;
  if (apiDay.error) return <PageError {...apiDay} />;

  const users = apiUsers.data?.['hydra:member'];
  const entries = apiDay.data?.['hydra:member'];
  if (!users || !entries) return <PageLoading />;

  const activity = buildDayActivity(Model.User.getAgencySorted(users), entries);
  // Archived clients are left out, as the nav leaves them out; any time logged
  // against one is drawn in grey rather than earning it a swatch.
  const companies = agencyCompanies
    .filter((company) => !company.archivedAt)
    .sort(Model.Company.compareCompanies);
  const colors = Model.Company.getChartColors(companies);
  const totalMinutes = Model.WorkDone.sumMinutes(
    Model.WorkDone.filterLogged(entries)
  );
  const runningCount = activity.filter((a) => a.running).length;

  return (
    <PageWrap background={Color.neutral5} noPadding limitWidth="small">
      <Helmet>
        <title>Daily Activity | Projects</title>
      </Helmet>

      <Stack padH={1.5} padV={1.5} gap={1}>
        <Text typeface="headline" as="h1">
          Projects
        </Text>

        <ProjectsTabs selected="activity" />

        <html.div style={[Style.row, Style.alignCenter, Style.spaceBetween]}>
          <ActivityDateSelect
            dates={getSelectableDates(today)}
            value={date}
            onChange={setDate}
          />
          <html.div style={[Style.row, Style.alignCenter, styles.summary]}>
            <Text typeface="body.small.medium" color="neutral60">
              {`${formatTimeSpent(totalMinutes) ?? '0m'} logged`}
            </Text>
            {runningCount > 0 && (
              <html.span
                style={[Style.row, Style.alignCenter, styles.runningCount]}
              >
                <html.span style={styles.dot} />
                <Text typeface="caption.medium" color="green">
                  {runningCount === 1
                    ? '1 timer running'
                    : `${runningCount} timers running`}
                </Text>
              </html.span>
            )}
          </html.div>
        </html.div>

        <html.div style={[Style.row, Style.wrap, styles.legend]}>
          {companies.map((company) => (
            <html.span
              key={company.id}
              style={[Style.row, Style.alignCenter, styles.legendItem]}
            >
              <html.span
                style={[styles.swatch, styles.swatchColor(colors[company.id])]}
              />
              <Text typeface="caption.medium" color="neutral60">
                {company.name}
              </Text>
            </html.span>
          ))}
          <Text typeface="caption.medium" color="neutral40">
            bar full width = 8h
          </Text>
        </html.div>

        <html.div style={styles.card}>
          {activity.map((userActivity, i) => (
            <React.Fragment key={userActivity.user.id}>
              {i > 0 && <html.span style={styles.rule} />}
              <ActivityUserRow
                activity={userActivity}
                colors={colors}
                now={now}
                isMe={userActivity.user.id === myUser.id}
                isToday={isToday}
              />
            </React.Fragment>
          ))}
        </html.div>
      </Stack>
    </PageWrap>
  );
}

const styles = css.create({
  summary: {
    gap: 8,
  },
  runningCount: {
    gap: 6,
    paddingInline: 8,
    paddingBlock: 2,
    borderRadius: 999,
    backgroundColor: color.greenBg,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 999,
    backgroundColor: color.green,
  },
  legend: {
    gap: 16,
    rowGap: 6,
    alignItems: 'center',
  },
  legendItem: {
    gap: 6,
  },
  swatch: {
    width: 8,
    height: 8,
    borderRadius: 999,
  },
  swatchColor: (backgroundColor: string) => ({ backgroundColor }),
  card: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: color.neutral0,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    borderRadius: 12,
  },
  rule: {
    height: 1,
    backgroundColor: color.neutral10,
  },
});
